/**
 * Grouping resolved rows into orders.
 *
 * The requested behaviour: rows with the same client and the same date become one order.
 * Two deliberate refinements:
 *
 * - Rows with NO client are never merged with each other. There is no key that says two
 *   anonymous purchases on the same day belong to one receipt, and merging unrelated
 *   walk-ins into a single sale would misstate the check count and average check.
 * - The payment method is part of the key. One sale carries one payment method, so rows
 *   that name different methods cannot be the same order.
 */

import { type ImportDate, type OrderDraft, type OrderDraftItem, type ResolvedSalesRow } from './types'

export interface GroupSalesOrdersOptions {
  /** The «объединять в один заказ» switch. When false, every row is its own order. */
  joinByClientAndDate: boolean
}

const dayKey = (date: ImportDate): string =>
  `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`

const timeValue = (date: ImportDate): number =>
  date.hour * 3600 + date.minute * 60 + date.second

/** The identity fields grouping needs — the rest of the product document is irrelevant here. */
interface ProductIdentity {
  id?: string
  firestoreId?: string
  barcode?: string | number
  name?: string
}

const productKey = (product: ProductIdentity | null | undefined): string =>
  String(product?.id ?? product?.firestoreId ?? product?.barcode ?? product?.name ?? '')

/**
 * The earliest wall-clock time in the group wins; a group where no row carried a time
 * stays date-only, which is what makes buildSaleInfo place it at midday store-local.
 */
const mergeDates = (current: ImportDate, incoming: ImportDate): ImportDate => {
  if (!incoming.hasTime) return current
  if (!current.hasTime) return incoming
  return timeValue(incoming) < timeValue(current) ? incoming : current
}

const addItem = (items: OrderDraftItem[], row: ResolvedSalesRow): void => {
  const key = productKey(row.product)
  const existing = items.find((item) => productKey(item.product) === key)
  if (existing) {
    // Same product listed twice in one order → one cart line with the summed quantity.
    existing.quantity += row.quantity
    existing.rowNumbers.push(row.rowNumber)
    return
  }
  items.push({ product: row.product, quantity: row.quantity, rowNumbers: [row.rowNumber] })
}

export const groupSalesOrders = (
  rows: ResolvedSalesRow[],
  { joinByClientAndDate }: GroupSalesOrdersOptions
): OrderDraft[] => {
  const orders: OrderDraft[] = []
  const byKey = new Map<string, OrderDraft>()

  rows.forEach((row, index) => {
    const mergeable = joinByClientAndDate && row.clientKey !== null
    const key = mergeable
      ? `${row.clientKey}|${dayKey(row.date)}|${row.paymentMethodKey ?? ''}`
      : `row-${index}`

    const existing = mergeable ? byKey.get(key) : undefined
    if (existing) {
      existing.date = mergeDates(existing.date, row.date)
      existing.rowNumbers.push(row.rowNumber)
      addItem(existing.items, row)
      return
    }

    const order: OrderDraft = {
      key,
      clientKey: row.clientKey,
      paymentMethodKey: row.paymentMethodKey,
      date: row.date,
      items: [],
      rowNumbers: [row.rowNumber],
    }
    addItem(order.items, row)
    orders.push(order)
    if (mergeable) byKey.set(key, order)
  })

  return orders
}
